// FILE LOCATION: src/pages/AdminGrants.tsx
// ──────────────────────────────────────────────────────────────
// Admin-only page for minting access grant codes and viewing
// existing ones. Gated to user IDs in VITE_PEERPREP_ADMIN_USER_IDS.
//
// Add the route: <Route path="/admin/grants" element={<AdminGrants />} />
// inside the ProtectedLayout block in App.tsx.
// ──────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback } from "react";
import { useUser } from "@clerk/clerk-react";
import { Link } from "react-router-dom";

const SUPABASE_FUNCTIONS_URL = `${
  import.meta.env.VITE_SUPABASE_URL
}/functions/v1`;

const ADMIN_USER_IDS = (import.meta.env.VITE_PEERPREP_ADMIN_USER_IDS || "")
  .split(",")
  .map((s: string) => s.trim())
  .filter(Boolean);

interface Grant {
  id: string;
  code: string;
  granted_tier: string;
  duration_days: number;
  max_redemptions: number | null;
  redemption_count: number;
  code_expires_at: string | null;
  notes: string | null;
  created_at: string;
}

function MintForm({
  onMinted,
  adminUserId,
}: {
  onMinted: () => void;
  adminUserId: string;
}) {
  const [code, setCode] = useState("");
  const [grantedTier, setGrantedTier] = useState<"essential" | "pro">(
    "essential"
  );
  const [durationDays, setDurationDays] = useState(30);
  const [maxRedemptions, setMaxRedemptions] = useState<string>(""); // blank = unlimited
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastCode, setLastCode] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/mint-access-grant`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          user_id: adminUserId,
          code: code.trim() || undefined,
          granted_tier: grantedTier,
          duration_days: durationDays,
          max_redemptions:
            maxRedemptions.trim() === "" ? null : Number(maxRedemptions),
          notes: notes.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error || "Mint failed");

      setLastCode(data.grant.code);
      setCode("");
      setNotes("");
      setMaxRedemptions("");
      onMinted();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={cardStyle}>
      <h2 style={h2Style}>Mint a new code</h2>

      <div style={fieldRowStyle}>
        <label style={labelStyle}>
          Code <span style={mutedStyle}>(leave blank to auto-generate)</span>
        </label>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="e.g. POTTER-2026"
          style={inputStyle}
        />
      </div>

      <div style={fieldRowStyle}>
        <label style={labelStyle}>Tier</label>
        <select
          value={grantedTier}
          onChange={(e) =>
            setGrantedTier(e.target.value as "essential" | "pro")
          }
          style={inputStyle}
        >
          <option value="essential">Essential</option>
          <option value="pro">Pro</option>
        </select>
      </div>

      <div style={fieldRowStyle}>
        <label style={labelStyle}>Duration (days)</label>
        <input
          type="number"
          min={1}
          max={365}
          value={durationDays}
          onChange={(e) => setDurationDays(Number(e.target.value))}
          style={inputStyle}
        />
      </div>

      <div style={fieldRowStyle}>
        <label style={labelStyle}>
          Max redemptions <span style={mutedStyle}>(blank = unlimited)</span>
        </label>
        <input
          type="number"
          min={1}
          value={maxRedemptions}
          onChange={(e) => setMaxRedemptions(e.target.value)}
          placeholder="unlimited"
          style={inputStyle}
        />
      </div>

      <div style={fieldRowStyle}>
        <label style={labelStyle}>Notes</label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Sent to Dr. Potter via LinkedIn DM"
          style={inputStyle}
        />
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      <button type="submit" disabled={busy} style={primaryButtonStyle}>
        {busy ? "Minting…" : "Mint code"}
      </button>

      {lastCode && (
        <div style={successCardStyle}>
          <div style={{ fontSize: 13, color: "#8b949e", marginBottom: 4 }}>
            Last minted code:
          </div>
          <div style={codeBigStyle}>{lastCode}</div>
          <div style={{ fontSize: 13, color: "#8b949e", marginTop: 8 }}>
            Magic link:
          </div>
          <div style={{ ...codeBigStyle, fontSize: 13, marginTop: 4 }}>
            {window.location.origin}/r/{lastCode}
          </div>
          <button
            type="button"
            onClick={() =>
              navigator.clipboard.writeText(
                `${window.location.origin}/r/${lastCode}`
              )
            }
            style={{ ...secondaryButtonStyle, marginTop: 8 }}
          >
            Copy magic link
          </button>
        </div>
      )}
    </form>
  );
}

function GrantsTable({
  grants,
  refetch,
}: {
  grants: Grant[];
  refetch: () => void;
}) {
  return (
    <div style={cardStyle}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2 style={h2Style}>Existing codes</h2>
        <button onClick={refetch} style={secondaryButtonStyle}>
          Refresh
        </button>
      </div>
      {grants.length === 0 ? (
        <div style={mutedStyle}>No codes minted yet.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Tier</th>
                <th style={thStyle}>Days</th>
                <th style={thStyle}>Redeemed</th>
                <th style={thStyle}>Code expires</th>
                <th style={thStyle}>Notes</th>
                <th style={thStyle}>Created</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {grants.map((g) => {
                const link = `${window.location.origin}/r/${g.code}`;
                const cap =
                  g.max_redemptions === null ? "∞" : g.max_redemptions;
                return (
                  <tr key={g.id}>
                    <td style={tdCodeStyle}>{g.code}</td>
                    <td style={tdStyle}>{g.granted_tier}</td>
                    <td style={tdStyle}>{g.duration_days}</td>
                    <td style={tdStyle}>
                      {g.redemption_count} / {cap}
                    </td>
                    <td style={tdStyle}>
                      {g.code_expires_at
                        ? new Date(g.code_expires_at).toLocaleDateString()
                        : "—"}
                    </td>
                    <td style={tdStyle}>{g.notes || "—"}</td>
                    <td style={tdStyle}>
                      {new Date(g.created_at).toLocaleDateString()}
                    </td>
                    <td style={tdStyle}>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard.writeText(link)}
                        style={smallButtonStyle}
                      >
                        Copy link
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminGrants() {
  const { user, isLoaded } = useUser();
  const [grants, setGrants] = useState<Grant[]>([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = !!user && ADMIN_USER_IDS.includes(user.id);

  const loadGrants = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/list-access-grants`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ user_id: user.id }),
      });
      const data = await res.json();
      setGrants(data.grants || []);
    } catch (err) {
      console.error("Failed to load grants:", err);
      setGrants([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (isAdmin) loadGrants();
  }, [isAdmin, loadGrants]);

  if (!isLoaded) return null;

  if (!isAdmin) {
    return (
      <div style={{ ...pageStyle, textAlign: "center" }}>
        <h1 style={{ ...h1Style, marginTop: 80 }}>Forbidden</h1>
        <p style={mutedStyle}>You don't have access to this page.</p>
        <Link
          to="/"
          style={{ color: "#2dd4bf", marginTop: 16, display: "inline-block" }}
        >
          Go home
        </Link>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <h1 style={h1Style}>Access grants</h1>
          <Link to="/" style={{ color: "#8b949e", fontSize: 13 }}>
            ← Back to dashboard
          </Link>
        </div>

        <MintForm onMinted={loadGrants} adminUserId={user!.id} />

        {loading ? (
          <div style={{ ...cardStyle, color: "#8b949e" }}>Loading…</div>
        ) : (
          <GrantsTable grants={grants} refetch={loadGrants} />
        )}
      </div>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  background: "#0e1117",
  color: "#e6edf3",
  padding: "32px 24px",
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

const cardStyle: React.CSSProperties = {
  background: "#161b22",
  border: "1px solid #30363d",
  borderRadius: 12,
  padding: 24,
  marginTop: 20,
};

const h1Style: React.CSSProperties = {
  margin: 0,
  fontSize: 28,
  fontWeight: 600,
};

const h2Style: React.CSSProperties = {
  margin: "0 0 16px",
  fontSize: 18,
  fontWeight: 600,
};

const fieldRowStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  marginBottom: 12,
};

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  color: "#8b949e",
};

const mutedStyle: React.CSSProperties = {
  color: "#6e7681",
  fontSize: 13,
};

const inputStyle: React.CSSProperties = {
  background: "#0d1117",
  border: "1px solid #30363d",
  borderRadius: 6,
  padding: "8px 12px",
  color: "#e6edf3",
  fontSize: 14,
  fontFamily: "inherit",
};

const errorStyle: React.CSSProperties = {
  background: "#3d1a1f",
  border: "1px solid #ef4444",
  borderRadius: 6,
  padding: "8px 12px",
  color: "#ef9999",
  fontSize: 13,
  marginBottom: 12,
};

const successCardStyle: React.CSSProperties = {
  marginTop: 16,
  background: "#0d2a26",
  border: "1px solid #2dd4bf",
  borderRadius: 8,
  padding: 12,
};

const codeBigStyle: React.CSSProperties = {
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: 16,
  color: "#2dd4bf",
  wordBreak: "break-all",
};

const primaryButtonStyle: React.CSSProperties = {
  background: "#2dd4bf",
  color: "#0e1117",
  border: "none",
  borderRadius: 6,
  padding: "10px 20px",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

const secondaryButtonStyle: React.CSSProperties = {
  background: "transparent",
  color: "#2dd4bf",
  border: "1px solid #2dd4bf",
  borderRadius: 6,
  padding: "6px 12px",
  fontSize: 13,
  cursor: "pointer",
};

const smallButtonStyle: React.CSSProperties = {
  background: "transparent",
  color: "#8b949e",
  border: "1px solid #30363d",
  borderRadius: 4,
  padding: "4px 8px",
  fontSize: 12,
  cursor: "pointer",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 12px",
  borderBottom: "1px solid #30363d",
  color: "#8b949e",
  fontWeight: 500,
};

const tdStyle: React.CSSProperties = {
  padding: "10px 12px",
  borderBottom: "1px solid #21262d",
};

const tdCodeStyle: React.CSSProperties = {
  ...tdStyle,
  fontFamily: '"JetBrains Mono", monospace',
  color: "#2dd4bf",
};
